import { useContext, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { JobsPanelState, JobsStatus } from "../../../constants/Jobs";
import { JobContext, UserContext, VersionContext } from "../../../Context";
import { triggerAlertsRefresh } from "../../../store/triggersSlice";
import {
    allJobObjectsAreValid,
    applyValidationResults,
    getJobRelatedObjects,
    jobValidationInProgress,
    objectsShouldValidate,
    updateJobAndSendToQA1,
} from "../../../utils/jobsUtils";
import InputField from "../../genericComponents/InputField/InputField";
import "./JobDetails.css";
import ApproveIcon from "../../../assets/jobs/approve.svg?react";
import CheckPinIcon from "../../../assets/jobs/checkPin.svg?react";
import { formatDate } from "@arcgis/core/intl";
import { StoreState } from "../../../store/rootReducer";
import { FeatureWithChanges, findDifferencesForPlan } from "../../../utils/newVersioningUtils";
import EditedFeaturesForPlan from "../EditedFeatures/EditedFeaturesForPlan";
import NfsButton from "../../genericComponents/NfsButton/NfsButton";
import { isDefaultVersion } from "../../../utils/versioningUtils";
import type Graphic from "@arcgis/core/Graphic";
import type { ResourceHandle } from "@arcgis/core/core/Handles";

interface JobPlanDetailsProps {
    jobObject: Graphic;
    setJobPanelState: (state: JobsPanelState) => void;
    setLoading: (loading: boolean) => void;
    storeHighlightHandle: (handle: ResourceHandle | null) => void;
    clearHighlightedFeatures: () => void;
}

const JobPlanDetails: React.FC<JobPlanDetailsProps> = ({
    jobObject,
    setJobPanelState,
    setLoading,
    storeHighlightHandle,
    clearHighlightedFeatures,
}) => {
    const dispatch = useDispatch();

    const [currentJobStep, setCurrentJobStep] = useState<Graphic | null>(null);
    const [jobFeaturesWithChanges, setJobFeaturesWithChanges] = useState<FeatureWithChanges[]>([]);
    const [comment, setComment] = useState<string | undefined>(undefined);
    const [isLoadingChanges, setIsLoadingChanges] = useState<boolean>(true);

    const activeVersionGuid = useSelector((state: StoreState) => state.version.activeGuid);
    const activeVersion = useSelector((state: StoreState) => state.version.versionInfo);

    const versionContext = useContext(VersionContext);
    const versionManagementRef = versionContext?.versionServiceRef.value;

    const userContext = useContext(UserContext);
    const user = userContext?.user.value;

    const jobsContext = useContext(JobContext);

    const validateObjects = objectsShouldValidate(jobObject.attributes.jobb_type);

    const returnToJobList = () => {
        setJobPanelState(JobsPanelState.LIST);
        jobsContext?.selectedJob.set(null);
    };

    const checkIfDefaultVersion = () => {
        if (versionManagementRef?.current && activeVersion) {
            return isDefaultVersion(activeVersion, versionManagementRef.current);
        }
    };

    const getValidationResults = async (objects: FeatureWithChanges[]) => {
        const versionName = activeVersion?.versionIdentifier.name ?? null;
        await applyValidationResults(objects, setJobFeaturesWithChanges, versionName, "PostToDefault");
    };

    const sendUpdatedJobToQA = async () => {
        setLoading(true);
        if (!versionManagementRef?.current.loaded) {
            await versionManagementRef?.current.load();
        }
        if ("{" + activeVersionGuid + "}" === versionManagementRef?.current.defaultVersionIdentifier.guid) {
            toast.error("Kan ikke oppdatere jobb i default versjon. Velg en annen versjon.");
            setLoading(false);
            return;
        }

        if (jobFeaturesWithChanges.length === 0) {
            toast.error("Ingen endringer funnet");
            setLoading(false);
            return;
        }

        if (jobObject) {
            const success = await updateJobAndSendToQA1(
                jobFeaturesWithChanges,
                currentJobStep?.attributes.globalid,
                comment ?? "",
                jobObject.attributes.globalid,
                jobObject.attributes.objectid,
                jobObject.attributes.navn,
                activeVersionGuid,
                activeVersion?.versionIdentifier.name ?? "sde.DEFAULT",
                user?.username ?? ""
            );

            if (success) {
                toast.success("Jobben er sendt til QA1 for godkjenning");

                dispatch(triggerAlertsRefresh());
                returnToJobList();
            }
        }
        setLoading(false);
    };

    const canIReview = () => {
        if (jobObject.attributes.status === JobsStatus.Plan && jobObject.attributes.opprettet_av === user?.username) {
            return true;
        } else {
            return false;
        }
    };

    const getObjectsConnectedToJob = async () => {
        const job = jobObject;
        const versionName = activeVersion?.versionIdentifier.name;
        const versionGuid = activeVersionGuid;

        if (job && versionName && versionGuid) {
            setIsLoadingChanges(true);
            const objects = await findDifferencesForPlan(job, versionGuid, versionName);
            console.log("Objects with changes from job:", objects);
            setJobFeaturesWithChanges(objects);
            setIsLoadingChanges(false);
            if (validateObjects) {
                getValidationResults(objects); // fire and forget
            }
        }
    };

    const validationInProgress = () => jobValidationInProgress(jobFeaturesWithChanges);

    const objectsAreValid = () => allJobObjectsAreValid(jobFeaturesWithChanges, validateObjects);

    useEffect(() => {
        getJobRelatedObjects(jobObject.attributes.objectid, "Steg").then((objects) => {
            // Find step before this step
            const orderedSteps = [...objects];
            // Sorts steps by opprettet_dato in descending order in case there are multiple steps with the same jobb_status
            orderedSteps.sort((a, b) => b.attributes.opprettet_dato - a.attributes.opprettet_dato);
            let activeStep;
            for (let step of orderedSteps) {
                if (step.attributes.jobb_status === jobObject.attributes.status) {
                    activeStep = step;
                    break;
                }
            }
            if (activeStep) {
                setCurrentJobStep(activeStep);
            }
        });
    }, [jobObject]);

    useEffect(() => {
        getObjectsConnectedToJob();
    }, []);

    return (
        <>
            <div className="job-details-body">
                <div className="job-details-body-content">
                    <label className="job-details-label">
                        <span className="hidden">{jobObject.attributes.globalid}</span>
                        <span>Jobbnavn</span>
                        <span>{jobObject.attributes.navn}</span>
                    </label>
                    <label className="job-details-label">
                        <span>Beskrivelse</span>
                        <span>{jobObject.attributes.beskrivelse}</span>
                    </label>
                    {canIReview() && (
                        <InputField
                            label="Kommentar til QA1"
                            onChange={setComment}
                            inputFieldId={jobObject.attributes.globalid + "comment"}
                            value={comment}
                            type="text"
                            isTextArea
                            textAreaRows={3}
                            maxLength={255}
                        />
                    )}

                    <div className="empty-button flex-row small-gap center">
                        <CheckPinIcon />
                        {`Objekter tilknyttet jobben (${jobFeaturesWithChanges.length})`}
                    </div>
                    <div className="flex-row space-between">
                        <span className="job-card-key">Eier</span>
                        <span className="job-card-value bold">{jobObject.attributes.opprettet_av}</span>
                    </div>
                    <div className="flex-row space-between">
                        <span className="job-card-key">Oppdatert</span>
                        <span className="job-card-value bold">{formatDate(jobObject.attributes.oppdatert_dato)}</span>
                    </div>
                </div>
                {(jobObject.attributes.status === JobsStatus.PlanIkkeGodkjent ||
                    jobObject.attributes.status === JobsStatus.Plan) && (
                    <EditedFeaturesForPlan
                        featuresAddedToJob={jobFeaturesWithChanges}
                        refreshFeaturesAddedToJob={getObjectsConnectedToJob}
                        isLoadingChanges={isLoadingChanges}
                        storeHighlightHandle={storeHighlightHandle}
                        clearHighlightedFeatures={clearHighlightedFeatures}
                    />
                )}
            </div>
            {!checkIfDefaultVersion() && (
                <div className="job-details-actions">
                    {validateObjects && validationInProgress() && (
                        <span className="job-details-actions-info">Validerer objekter tilknyttet jobben...</span>
                    )}
                    {validateObjects && !validationInProgress() && !objectsAreValid() && (
                        <span className="job-details-actions-warning">
                            Noen objekter har valideringsfeil. Rett feilene før du sender jobben videre.
                        </span>
                    )}
                    <div className="job-details-actions-buttons">
                        <NfsButton
                            className="nfs-button"
                            disabled={isLoadingChanges || !objectsAreValid()}
                            onClick={sendUpdatedJobToQA}
                        >
                            <ApproveIcon />
                            Oppdater og send til QA1
                        </NfsButton>
                    </div>
                </div>
            )}
            {checkIfDefaultVersion() && (
                <div className="job-details-actions">
                    <span>Plan kan kun sendes til QA1 fra versjon</span>
                </div>
            )}
        </>
    );
};

export default JobPlanDetails;
